import type {
  AddLhcItemsBody,
  AllocateMutationBody,
  CollectionBiltoDetailResponse,
  CollectionMutationDetailResponse,
  CollectionPaymentProofDetailResponse,
  LhcDeliveryOrdersResponse,
  LHCDetails,
  LhcFilter,
  LhcItemMutationsFilter,
  LhcItemMutationsResponse,
  LhcItemsFilter,
  LhcItemsResponse,
  LhcListResponse,
  RelatedDeliveryOrdersResponse,
} from '@diamond/shared/types';
import { apiInstance } from '@diamond/shared/utils';

import type {
  CollectionPlantsResponse,
  CreateLHCPayload,
  DuplicateLHCPayload,
  LHCDetailInvoiceRowData,
  LHCRowData,
  UpdateLHCPayload,
} from '../types';
import { COLLECTION_API_ENDPOINTS } from './constants';
import { mapInvoiceItemsToRows, mapLHCItemToRowData } from './mappers';

export { mapInvoiceItemsToRows } from './mappers';

/**
 * Maps LHC items API response to flat table rows (parent + child).
 * Use this when you have LhcItemsResponse from getLHCDetail.
 */
export function getLHCItemsAsRows(
  response: LhcItemsResponse
): LHCDetailInvoiceRowData[] {
  const plantName = response.lhc.plant_name || '';
  return mapInvoiceItemsToRows(response.data, plantName);
}

export interface LHCListResult {
  data: LHCRowData[];
  pagination: {
    rows: number;
    pages: number;
  };
  total: number;
  totalItems: number;
}

export async function getLHCList(
  token: string,
  params: LhcFilter
): Promise<LHCListResult> {
  const response = await apiInstance(token).get<LhcListResponse>(
    COLLECTION_API_ENDPOINTS.LHC_LIST,
    { params }
  );

  const pageOffset = (params.page - 1) * params.size;

  return {
    data: response.data.data.map((item, index) =>
      mapLHCItemToRowData(item as LHCDetails, index, pageOffset)
    ),
    pagination: response.data.pagination,
    total: response.data.total,
    totalItems: response.data.total_items,
  };
}

export async function getCollectionPlants(
  token: string,
  searchQuery?: string
): Promise<CollectionPlantsResponse> {
  const response = await apiInstance(token).get<CollectionPlantsResponse>(
    COLLECTION_API_ENDPOINTS.COLLECTION_PLANTS,
    { params: searchQuery ? { search_query: searchQuery } : undefined }
  );
  return response.data;
}

export async function getLHCDetail(
  token: string,
  id: string,
  params?: LhcItemsFilter
): Promise<LhcItemsResponse> {
  const response = await apiInstance(token).get<LhcItemsResponse>(
    COLLECTION_API_ENDPOINTS.LHC_DETAIL(id),
    { params }
  );
  return response.data;
}

export async function searchLHCDeliveryOrders(
  token: string,
  lhcId: string,
  searchQuery: string
): Promise<LhcDeliveryOrdersResponse> {
  const response = await apiInstance(token).get<LhcDeliveryOrdersResponse>(
    COLLECTION_API_ENDPOINTS.LHC_DELIVERY_ORDERS(lhcId),
    {
      params: {
        search_query: searchQuery,
      },
    }
  );
  return response.data;
}

export type SearchLHCItemMutationsParams = LhcItemMutationsFilter;

export interface CollectionBiltoDetailParams {
  page?: number;
  limit?: number;
  start_date?: string;
  end_date?: string;
  type?: 'billing' | 'bank-mutation';
}

export async function searchLHCItemMutations(
  token: string,
  lhcId: string,
  itemId: string,
  params: SearchLHCItemMutationsParams
): Promise<LhcItemMutationsResponse> {
  const response = await apiInstance(token).get<LhcItemMutationsResponse>(
    COLLECTION_API_ENDPOINTS.LHC_ITEM_MUTATIONS(lhcId, itemId),
    { params }
  );
  return response.data;
}

export async function allocateLHCItemMutations(
  token: string,
  lhcId: string,
  itemId: string,
  payload: AllocateMutationBody
): Promise<{ message: string }> {
  const response = await apiInstance(token).post<{ message: string }>(
    COLLECTION_API_ENDPOINTS.LHC_ITEM_MUTATIONS(lhcId, itemId),
    payload
  );
  return response.data;
}

export async function replaceLHCItemMutations(
  token: string,
  lhcId: string,
  itemId: string,
  payload: AllocateMutationBody
): Promise<{ message: string }> {
  const response = await apiInstance(token).put<{ message: string }>(
    COLLECTION_API_ENDPOINTS.LHC_ITEM_MUTATIONS(lhcId, itemId),
    payload
  );
  return response.data;
}

export async function getCollectionPaymentProofDetail(
  token: string,
  paymentProofId: string,
  orderMetaId: string
): Promise<CollectionPaymentProofDetailResponse> {
  const response = await apiInstance(
    token
  ).get<CollectionPaymentProofDetailResponse>(
    COLLECTION_API_ENDPOINTS.COLLECTION_PAYMENT_PROOF_DETAIL(
      paymentProofId,
      orderMetaId
    )
  );
  return response.data;
}

export async function getCollectionMutationDetail(
  token: string,
  mutationId: string,
  orderMetaId: string
): Promise<CollectionMutationDetailResponse> {
  const response = await apiInstance(
    token
  ).get<CollectionMutationDetailResponse>(
    COLLECTION_API_ENDPOINTS.COLLECTION_MUTATION_DETAIL(mutationId, orderMetaId)
  );
  return response.data;
}

export async function getCollectionBiltoDetail(
  token: string,
  sapNoBilto: string,
  params?: CollectionBiltoDetailParams
): Promise<CollectionBiltoDetailResponse> {
  const response = await apiInstance(token).get<CollectionBiltoDetailResponse>(
    COLLECTION_API_ENDPOINTS.COLLECTION_BILTO_DETAIL(sapNoBilto),
    { params }
  );
  return response.data;
}

export async function getRelatedDeliveryOrders(
  token: string,
  lhcId: string,
  poNumber: string,
  excludeDeliveryOrderId?: string
): Promise<RelatedDeliveryOrdersResponse> {
  const response = await apiInstance(token).get<RelatedDeliveryOrdersResponse>(
    COLLECTION_API_ENDPOINTS.LHC_RELATED_DELIVERY_ORDERS(lhcId),
    {
      params: {
        po_number: poNumber,
        ...(excludeDeliveryOrderId
          ? { exclude_delivery_order_id: excludeDeliveryOrderId }
          : {}),
      },
    }
  );
  return response.data;
}

export async function addLHCItems(
  token: string,
  lhcId: string,
  deliveryOrderIds: string[]
): Promise<{ count: number; message: string }> {
  const payload: AddLhcItemsBody = { delivery_order_ids: deliveryOrderIds };
  const response = await apiInstance(token).post<{
    count: number;
    message: string;
  }>(COLLECTION_API_ENDPOINTS.LHC_ADD_ITEMS(lhcId), payload);
  return response.data;
}

export async function createLHC(
  token: string,
  payload: CreateLHCPayload
): Promise<LHCDetails> {
  const response = await apiInstance(token).post<LHCDetails>(
    COLLECTION_API_ENDPOINTS.CREATE_LHC,
    payload
  );
  return response.data;
}

export async function updateLHC(
  token: string,
  id: string,
  payload: UpdateLHCPayload
): Promise<LHCDetails> {
  const response = await apiInstance(token).patch<LHCDetails>(
    COLLECTION_API_ENDPOINTS.UPDATE_LHC(id),
    payload
  );
  return response.data;
}

export interface ExportLHCResponse {
  data: Blob;
  filename: string;
}

export async function exportLHC(
  token: string,
  id: string
): Promise<ExportLHCResponse> {
  const response = await apiInstance(token).get(
    COLLECTION_API_ENDPOINTS.LHC_EXPORT(id),
    {
      responseType: 'blob',
    }
  );

  const disposition = response.headers['content-disposition'] || '';
  const quotedMatch = disposition.match(/filename="([^"]+)"/);
  const unquotedMatch = disposition.match(/filename=([^;\s]+)/);
  const filename = quotedMatch?.[1] || unquotedMatch?.[1] || `lhc-${id}.xlsx`;

  return {
    data: response.data,
    filename,
  };
}

export async function deleteLHCItem(
  token: string,
  lhcId: string,
  itemId: string
): Promise<{ message: string }> {
  const response = await apiInstance(token).delete<{ message: string }>(
    COLLECTION_API_ENDPOINTS.LHC_DELETE_ITEM(lhcId, itemId)
  );
  return response.data;
}

export async function reorderLHCItem(
  token: string,
  lhcId: string,
  itemId: string,
  index: number
): Promise<{ message: string }> {
  const response = await apiInstance(token).patch<{ message: string }>(
    COLLECTION_API_ENDPOINTS.LHC_ITEM_REORDER(lhcId, itemId),
    { index }
  );
  return response.data;
}

export async function duplicateLHC(
  token: string,
  id: string,
  payload: DuplicateLHCPayload
): Promise<LHCDetails> {
  const response = await apiInstance(token).post<LHCDetails>(
    COLLECTION_API_ENDPOINTS.LHC_DUPLICATE(id),
    payload
  );
  return response.data;
}

export async function deleteLHC(
  token: string,
  id: string
): Promise<{ message: string }> {
  const response = await apiInstance(token).delete<{ message: string }>(
    COLLECTION_API_ENDPOINTS.LHC_DELETE(id)
  );
  return response.data;
}

export interface UpdateLHCItemRemarkPayload {
  remark: string;
}

export interface UpdateLHCItemRemarkResponse {
  id: string;
  display_order: number;
  delivery_order_number: string;
  invoice_no: string;
  remark: string;
  updated_at: number;
}

export async function updateLHCItemRemark(
  token: string,
  lhcId: string,
  itemId: string,
  payload: UpdateLHCItemRemarkPayload
): Promise<UpdateLHCItemRemarkResponse> {
  const response = await apiInstance(token).patch<UpdateLHCItemRemarkResponse>(
    COLLECTION_API_ENDPOINTS.LHC_ITEM_REMARK(lhcId, itemId),
    payload
  );
  return response.data;
}
